import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  CheckIcon,
  Cross2Icon,
  DragHandleDots2Icon,
  PlusIcon,
} from '@radix-ui/react-icons'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useToast } from '@/components/ui/use-toast'
import Loader from '@/components/ui/Loader'
import { cn } from '@/lib/utils'
import { putFetcher } from 'api/swr'
import Selector from 'components/ui/Selector'
import ResultRateDialog from './ResultRateDialog'
import { TAG_I18N_LABELS } from 'commons/tagI18n'
import {
  DEFAULT_RANKING_COUNTRY,
  HOT_WORD_LANGS,
  HOT_WORD_MAX_COUNT,
  HOT_WORD_MAX_LENGTH,
  RANKING_COUNTRY_OPTIONS,
  SEARCH_RANKING_LIMIT,
  emptyPools,
  langOfCountry,
  langOptionLabel,
  type HotWordLang,
  type HotWordPools,
  type SearchRanking,
} from './constants'

function SortableHotWordItem({
  index,
  word,
  onRemove,
}: {
  index: number
  word: string
  onRemove: () => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: word })

  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={cn(
        'flex items-center gap-2 rounded-md border bg-background px-2 py-1.5 text-sm',
        isDragging && 'relative z-10 opacity-60 shadow-md',
      )}
    >
      <button
        type="button"
        className="flex h-7 w-7 shrink-0 cursor-grab items-center justify-center rounded text-muted-foreground active:cursor-grabbing"
        title="拖拽排序"
        {...attributes}
        {...listeners}
      >
        <DragHandleDots2Icon className="h-4 w-4" />
      </button>
      <span className="w-7 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
        {index}
      </span>
      <span className="min-w-0 flex-1 truncate">{word}</span>
      <button
        type="button"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
        title="删除"
        onClick={onRemove}
      >
        <Cross2Icon className="h-3.5 w-3.5" />
      </button>
    </li>
  )
}

const SearchHotWords: React.FC = () => {
  const { toast } = useToast()
  const { data, isLoading, mutate } = useSWR<HotWordPools>(['/search-hot-words'])
  const [lang, setLang] = useState<HotWordLang>('en')
  const [rankingCountry, setRankingCountry] = useState(DEFAULT_RANKING_COUNTRY)
  const [pools, setPools] = useState<HotWordPools>(emptyPools)
  const [draft, setDraft] = useState('')
  const [analysisOpen, setAnalysisOpen] = useState(false)

  useEffect(() => {
    if (!data) return
    const next = emptyPools()
    for (const key of HOT_WORD_LANGS) {
      next[key] = Array.isArray(data[key]) ? data[key] : []
    }
    setPools(next)
  }, [data])

  const { trigger: save, isMutating } = useSWRMutation(
    '/search-hot-words',
    putFetcher,
  )

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const currentWords = pools[lang]
  const currentKeys = useMemo(
    () => new Set(currentWords.map((word) => word.toLowerCase())),
    [currentWords],
  )

  const rankingShort = useMemo(
    () =>
      RANKING_COUNTRY_OPTIONS.find((item) => item.code === rankingCountry)
        ?.short,
    [rankingCountry],
  )
  const {
    data: ranking,
    isLoading: rankingLoading,
    error: rankingError,
  } = useSWR<SearchRanking>(
    rankingShort
      ? ['/search-hot-words/ranking', { country: rankingShort }]
      : null,
  )
  const rankingRows = ranking?.country === rankingShort ? ranking.rows : []
  const rankingLang = langOfCountry(rankingCountry)
  const rankingErrorMessage =
    rankingError instanceof Error
      ? rankingError.message
      : rankingError
        ? String(rankingError)
        : ''

  const addWord = useCallback(
    (raw: string, options?: { silentExist?: boolean }) => {
      const word = raw.trim()
      if (!word) return false
      if (word.length > HOT_WORD_MAX_LENGTH) {
        toast({
          variant: 'destructive',
          title: '添加失败',
          description: `单词语最多 ${HOT_WORD_MAX_LENGTH} 字`,
        })
        return false
      }
      if (currentKeys.has(word.toLowerCase())) {
        if (!options?.silentExist) {
          toast({
            title: '已在列表中',
            description: word,
          })
        }
        return false
      }
      if (currentWords.length >= HOT_WORD_MAX_COUNT) {
        toast({
          variant: 'destructive',
          title: '添加失败',
          description: `${TAG_I18N_LABELS[lang]} 最多 ${HOT_WORD_MAX_COUNT} 个词`,
        })
        return false
      }
      setPools((prev) => ({
        ...prev,
        [lang]: [...prev[lang], word],
      }))
      return true
    },
    [currentKeys, currentWords.length, lang, toast],
  )

  const onAddDraft = useCallback(() => {
    if (addWord(draft)) {
      setDraft('')
    }
  }, [addWord, draft])

  const onRemove = useCallback(
    (word: string) => {
      setPools((prev) => ({
        ...prev,
        [lang]: prev[lang].filter((item) => item !== word),
      }))
    },
    [lang],
  )

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      setPools((prev) => {
        const list = prev[lang]
        const oldIndex = list.findIndex((item) => item === active.id)
        const newIndex = list.findIndex((item) => item === over.id)
        if (oldIndex < 0 || newIndex < 0) return prev
        return { ...prev, [lang]: arrayMove(list, oldIndex, newIndex) }
      })
    },
    [lang],
  )

  const onSave = useCallback(async () => {
    try {
      const saved = await save(pools)
      await mutate(saved, false)
      toast({
        title: '已保存',
        description: HOT_WORD_LANGS.map(
          (key) => `${TAG_I18N_LABELS[key]} ${pools[key].length}`,
        ).join(' · '),
      })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err)
      toast({
        variant: 'destructive',
        title: '保存失败',
        description: message,
      })
    }
  }, [mutate, pools, save, toast])

  if (isLoading && !data) {
    return <Loader />
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-3 flex shrink-0 items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          按语言配置热词，拖拽排序。英语国家只下发英语；日 / 西 / 葡 /
          德会附带英语词。最多 {HOT_WORD_MAX_COUNT} 个、单词语{' '}
          {HOT_WORD_MAX_LENGTH} 字。改完后 Mask 接口最多 1 天才更新。
        </p>
        <Button onClick={onSave} disabled={isMutating}>
          {isMutating ? '保存中…' : '保存'}
        </Button>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden lg:grid-cols-2">
        <section className="flex min-h-0 flex-col rounded-lg border bg-background p-3">
          <div className="mb-3 flex shrink-0 flex-wrap items-center gap-2">
            <Select
              value={lang}
              onValueChange={(value) => setLang(value as HotWordLang)}
            >
              <SelectTrigger className="w-[280px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HOT_WORD_LANGS.map((key) => (
                  <SelectItem key={key} value={key}>
                    {langOptionLabel(key)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              className="min-w-[160px] flex-1"
              placeholder="输入新词，回车添加"
              value={draft}
              maxLength={HOT_WORD_MAX_LENGTH}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  onAddDraft()
                }
              }}
            />
            <Button type="button" variant="secondary" onClick={onAddDraft}>
              添加
            </Button>
          </div>

          <div className="mb-2 flex shrink-0 items-center justify-between text-sm">
            <span className="font-medium">
              {TAG_I18N_LABELS[lang]} 热词
            </span>
            <span className="text-muted-foreground">
              {currentWords.length} / {HOT_WORD_MAX_COUNT}
            </span>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            {currentWords.length === 0 ? (
              <div className="flex h-full min-h-[200px] items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                还没有词，在上方录入或从右侧排行榜添加
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
              >
                <SortableContext
                  items={currentWords}
                  strategy={verticalListSortingStrategy}
                >
                  <ul className="flex flex-col gap-1.5">
                    {currentWords.map((word, index) => (
                      <SortableHotWordItem
                        key={word}
                        index={index + 1}
                        word={word}
                        onRemove={() => onRemove(word)}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </section>

        <section className="flex min-h-0 flex-col rounded-lg border bg-background p-3">
          <div className="mb-3 flex shrink-0 items-center justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h3 className="text-sm font-medium">用户搜索排行榜</h3>
              <Selector
                datas={RANKING_COUNTRY_OPTIONS}
                labelName="Lang"
                value={rankingCountry}
                update={setRankingCountry}
                showCode
                width="w-[220px]"
              />
              <span className="text-xs text-muted-foreground">
                下发语言 {TAG_I18N_LABELS[rankingLang]}
              </span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => setAnalysisOpen(true)}
            >
              AI 分析
            </Button>
          </div>
          <ResultRateDialog
            open={analysisOpen}
            onOpenChange={setAnalysisOpen}
            initialCountry={rankingCountry}
            initialDate={
              ranking?.country === rankingShort && ranking.date
                ? ranking.date
                : ''
            }
          />
          <p className="mb-2 shrink-0 text-xs text-muted-foreground">
            Neku Android{' '}
            {ranking?.country === rankingShort && ranking.date
              ? ranking.date
              : '已结束的 metric 日'}{' '}
            的搜索词。日期按北京时间 16:00 切天，用已经结束的那一天。先取次数最高的{' '}
            {SEARCH_RANKING_LIMIT} 条，只把这 {SEARCH_RANKING_LIMIT}{' '}
            条里大小写、表情、标点不同，或者单词相同但顺序不同的词合并，次数相加，展示次数最高的写法。点添加加入当前左侧语言。
          </p>

          <div className="min-h-0 flex-1 overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-background">
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>热词</TableHead>
                  <TableHead className="w-24">搜索次数</TableHead>
                  <TableHead className="w-20 text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rankingLoading && rankingRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                      加载中…
                    </TableCell>
                  </TableRow>
                ) : rankingErrorMessage ? (
                  <TableRow>
                    <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                      {rankingErrorMessage}
                    </TableCell>
                  </TableRow>
                ) : rankingRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                      这一天这个国家没有搜索词
                    </TableCell>
                  </TableRow>
                ) : null}
                {rankingRows.map((row) => {
                  const added = currentKeys.has(row.word.toLowerCase())
                  return (
                    <TableRow key={`${rankingCountry}-${row.word}`}>
                      <TableCell className="tabular-nums text-muted-foreground">
                        {row.rank}
                      </TableCell>
                      <TableCell className="font-medium">{row.word}</TableCell>
                      <TableCell className="tabular-nums">
                        {row.count.toLocaleString('en-US')}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          disabled={added}
                          onClick={() =>
                            addWord(row.word, { silentExist: true })
                          }
                        >
                          {added ? (
                            <>
                              <CheckIcon className="mr-1 h-3.5 w-3.5" />
                              已添加
                            </>
                          ) : (
                            <>
                              <PlusIcon className="mr-1 h-3.5 w-3.5" />
                              添加
                            </>
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        </section>
      </div>
    </div>
  )
}

export default SearchHotWords
